import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Shield, Plus, Trash2, RefreshCw, UserCheck, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Email validation schema
const emailSchema = z.string()
  .trim()
  .email({ message: "Invalid email address" })
  .max(255, { message: "Email must be less than 255 characters" })
  .toLowerCase();

interface AdminUser {
  id: string;
  email_masked: string;
  email_full: string | null;
  created_at: string;
  is_current_user: boolean;
}

const AdminAccessManagement = () => {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string>('');
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [adminToRemove, setAdminToRemove] = useState<AdminUser | null>(null);

  const fetchAdminUsers = async () => {
    try {
      setLoading(true);
      
      // Use secure function that masks emails
      const { data, error } = await supabase.rpc('get_admin_list_secure');

      if (error) {
        console.error('Error fetching admin list');
        throw error;
      }

      setAdminUsers(data || []);
      
    } catch (error: any) {
      console.error('Error loading admin users');
      toast.error('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const validateEmail = (email: string): boolean => {
    try {
      emailSchema.parse(email);
      setEmailError('');
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        setEmailError(error.errors[0].message);
      }
      return false;
    }
  };

  const addAdminUser = async () => {
    try {
      const trimmedEmail = newAdminEmail.trim().toLowerCase();
      
      if (!validateEmail(trimmedEmail)) {
        return;
      }

      setAdding(true);

      // Use secure function to add admin
      const { data, error } = await supabase.rpc('add_admin_user_secure', {
        admin_email: trimmedEmail
      });

      if (error) {
        console.error('Error adding admin user');
        throw error;
      }

      // Refresh the list to get masked version
      await fetchAdminUsers();
      setNewAdminEmail('');
      setEmailError('');
      
      toast.success('Admin access granted successfully');

    } catch (error: any) {
      console.error('Error adding admin user');
      // Don't expose raw error messages that might contain email info
      if (error.message?.includes('already has admin access')) {
        toast.error('This email already has admin access');
      } else if (error.message?.includes('Invalid email format')) {
        toast.error('Invalid email format');
      } else {
        toast.error('Failed to add admin user');
      }
    } finally {
      setAdding(false);
    }
  };

  const confirmRemoveAdmin = (admin: AdminUser) => {
    setAdminToRemove(admin);
    setShowRemoveDialog(true);
  };

  const removeAdminUser = async () => {
    if (!adminToRemove) return;
    
    try {
      // For current user, use full email; otherwise this will fail (security feature)
      const emailToUse = adminToRemove.email_full || adminToRemove.email_masked;
      
      // Only allow removing yourself or if you know the full email
      if (!adminToRemove.is_current_user && !adminToRemove.email_full) {
        toast.error('Cannot remove other admins - contact them directly');
        return;
      }
      
      setRemoving(adminToRemove.id);

      const { error } = await supabase.rpc('remove_admin_user_secure', {
        admin_email: emailToUse
      });

      if (error) {
        console.error('Error removing admin user');
        throw error;
      }

      // Update local state
      setAdminUsers(prev => prev.filter(admin => admin.id !== adminToRemove.id));
      
      toast.success('Admin access revoked successfully');

    } catch (error: any) {
      console.error('Error removing admin user');
      if (error.message?.includes('Cannot remove the last admin')) {
        toast.error('Cannot remove the last admin');
      } else {
        toast.error('Failed to remove admin user');
      }
    } finally {
      setRemoving(null);
      setShowRemoveDialog(false);
      setAdminToRemove(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !adding && newAdminEmail.trim()) {
      addAdminUser();
    }
  };

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-purple-400" />
            <div>
              <CardTitle className="text-white text-xl">Admin Access Management</CardTitle>
              <p className="text-sm text-gray-400 mt-1">
                Grant or revoke admin privileges by email address
              </p>
            </div>
          </div>
          <Button
            onClick={fetchAdminUsers}
            disabled={loading}
            variant="outline"
            size="sm"
            className="border-purple-500/50 hover:bg-purple-500/10"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Add New Admin Section */}
        <div className="space-y-3">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-green-400" />
            Grant Admin Access
          </h3>
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                type="email"
                placeholder="Enter email address..."
                value={newAdminEmail}
                onChange={(e) => {
                  setNewAdminEmail(e.target.value);
                  if (emailError) {
                    validateEmail(e.target.value.trim());
                  }
                }}
                onBlur={() => {
                  if (newAdminEmail.trim()) {
                    validateEmail(newAdminEmail.trim());
                  }
                }}
                onKeyPress={handleKeyPress}
                disabled={adding}
                className={`bg-slate-800/50 border-purple-500/30 text-white ${
                  emailError ? 'border-red-500' : ''
                }`}
              />
              {emailError && (
                <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {emailError}
                </p>
              )}
            </div>
            <Button
              onClick={addAdminUser}
              disabled={adding || !newAdminEmail.trim() || !!emailError}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              {adding ? 'Adding...' : 'Add Admin'}
            </Button>
          </div>
          <p className="text-xs text-gray-400">
            💡 Any user with this email will have full admin access when they log in
          </p>
        </div>

        {/* Admin Users List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold">
              Current Admins ({adminUsers.length})
            </h3>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-400" />
              <p className="text-gray-400 mt-2">Loading admin users...</p>
            </div>
          ) : adminUsers.length === 0 ? (
            <div className="text-center py-8 bg-slate-800/30 rounded-lg border border-purple-500/20">
              <Shield className="w-12 h-12 mx-auto text-gray-500 mb-3" />
              <p className="text-gray-400">No admin users found</p>
              <p className="text-sm text-gray-500 mt-1">Add the first admin user above</p>
            </div>
          ) : (
            <div className="rounded-lg border border-purple-500/20 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-800/50 hover:bg-slate-800/50 border-purple-500/20">
                    <TableHead className="text-gray-300">Email</TableHead>
                    <TableHead className="text-gray-300">Added</TableHead>
                    <TableHead className="text-gray-300">Status</TableHead>
                    <TableHead className="text-right text-gray-300">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adminUsers.map((admin) => (
                    <TableRow
                      key={admin.id}
                      className="bg-slate-800/20 hover:bg-slate-800/40 border-purple-500/10"
                    >
                      <TableCell className="text-white font-medium">
                        <div className="flex items-center gap-2">
                          {admin.is_current_user ? (
                            <>
                              <Eye className="w-4 h-4 text-green-400" />
                              {admin.email_full}
                              <Badge variant="outline" className="text-xs bg-green-500/10 text-green-400 border-green-500/30">
                                You
                              </Badge>
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-4 h-4 text-gray-500" />
                              <span className="text-gray-300">{admin.email_masked}</span>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {new Date(admin.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          <Shield className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          onClick={() => confirmRemoveAdmin(admin)}
                          disabled={
                            removing === admin.id || 
                            adminUsers.length === 1 ||
                            (!admin.is_current_user && !admin.email_full)
                          }
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          title={
                            adminUsers.length === 1 
                              ? "Cannot remove the last admin" 
                              : !admin.is_current_user && !admin.email_full
                                ? "Can only remove yourself"
                                : "Remove admin access"
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Security Notice */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-amber-200 font-semibold mb-1">Security Notice</p>
              <ul className="text-amber-300/80 space-y-1 list-disc list-inside">
                <li>Admin users have full access to all system functions</li>
                <li>Only grant admin access to trusted individuals</li>
                <li>All admin actions are logged for security auditing</li>
                <li>Other admin emails are masked for privacy protection</li>
                <li>Cannot remove the last admin to prevent lockout</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>

      {/* Remove Confirmation Dialog */}
      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent className="bg-slate-900 border-purple-500/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Remove Admin Access?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to revoke admin access from{' '}
              <span className="text-white font-semibold">
                {adminToRemove?.is_current_user ? 'yourself' : adminToRemove?.email_masked}
              </span>?
              <br /><br />
              {adminToRemove?.is_current_user && (
                <span className="text-amber-400">
                  ⚠️ Warning: You are removing your own admin access!
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowRemoveDialog(false);
                setAdminToRemove(null);
              }}
              className="bg-slate-800 text-white hover:bg-slate-700"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={removeAdminUser}
              disabled={removing === adminToRemove?.id}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {removing === adminToRemove?.id ? 'Removing...' : 'Remove Access'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default AdminAccessManagement;